import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { 
  Email, 
  ArrowBack,
  VerifiedUser,
  Refresh
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import toast from 'react-hot-toast';

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyOTP, resendOTP } = useContext(AuthContext);
  
  const [email, setEmail] = useState(location.state?.email || '');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email) {
      toast.error('Session expired. Please log in again.');
      navigate('/login');
    }
  }, [email, navigate]);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleOtpChange = (index, value) => {
    if (isNaN(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    // Auto focus next
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      toast.error('Please enter the full 6-digit OTP');
      return;
    }

    setLoading(true);
    const result = await verifyOTP(email, otpString);
    setLoading(false);

    if (result.success) {
      navigate('/');
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    
    setResending(true);
    const result = await resendOTP(email);
    setResending(false);

    if (result.success) {
      setTimer(60);
      setCanResend(false);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0].focus();
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ mb: 4 }}>
          <Button 
            startIcon={<ArrowBack />} 
            onClick={() => navigate('/login')}
            sx={{ color: '#00D395' }}
          >
            Back to login
          </Button>
        </Box>

        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <Box 
              sx={{ 
                width: 60, 
                height: 60, 
                borderRadius: '50%', 
                bgcolor: 'rgba(0, 211, 149, 0.1)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto',
                mb: 2
              }}
            >
              <VerifiedUser sx={{ fontSize: 30, color: '#00D395' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              Verify Your Email
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We've sent a 6-digit verification code to <br />
              <strong style={{ color: '#fff' }}>{email}</strong>
            </Typography>
          </Box>

          <form onSubmit={handleVerify}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 4 }}>
              {otp.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  variant="outlined"
                  inputProps={{ 
                    maxLength: 1, 
                    style: { textAlign: 'center', fontSize: '1.5rem', fontWeight: 'bold', width: '40px' } 
                  }}
                  autoFocus={index === 0}
                />
              ))}
            </Box>

            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  bgcolor: '#00D395',
                  fontWeight: 'bold',
                  py: 1.5,
                  mb: 3,
                  '&:hover': { bgcolor: '#00b884' }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Account'}
              </Button>
            </motion.div>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Didn't receive the code?
              </Typography>
              <Button
                onClick={handleResend}
                disabled={!canResend || resending}
                startIcon={resending ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                sx={{ 
                  color: canResend ? '#00D395' : 'text.disabled',
                  textTransform: 'none',
                  fontWeight: 'bold'
                }}
              >
                {canResend ? 'Resend New Code' : `Resend in ${timer}s`}
              </Button>
            </Box>
          </form>
        </Paper>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Alert severity="info" variant="outlined" sx={{ borderColor: 'rgba(0, 211, 149, 0.3)', color: 'rgba(255,255,255,0.7)' }}>
            Please check your spam folder if you don't see the email in your inbox.
          </Alert>
        </Box>
      </motion.div>
    </Container>
  );
};

export default VerifyEmailPage;
